<p-toast position="top-center"></p-toast>
@if (isLoading) {
    <p-progressBar mode="indeterminate" class="m-2"></p-progressBar>
}

<app-page-shell>
    <app-page-hero eyebrow="Orders" title="Sales Orders" subtitle="Searchable sales order workbench">
        <div actions class="flex gap-2 flex-wrap">
            <p-button label="Export" icon="pi pi-download" severity="secondary" [outlined]="true" (click)="exportOrders()"></p-button>
            <p-button label="Add Sales Order" icon="pi pi-plus" (click)="addSalesOrder()"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <div class="saved-view-row">
        <span class="saved-view-label">Views</span>
        <button pButton type="button" label="All" class="p-button-sm p-button-text"
                [class.p-button-outlined]="activePresetView === 'ALL'"
                (click)="clearSearch()"></button>
        @for (view of presetViews; track view.key) {
            <button pButton type="button" [label]="view.label" class="p-button-sm"
                    [class.p-button-text]="activePresetView !== view.key"
                    (click)="applyPresetView(view)"></button>
        }
        @for (view of savedViews; track view.savedViewId) {
            <button pButton type="button" [label]="view.name" class="p-button-sm"
                    [class.p-button-text]="activeSavedViewId !== view.savedViewId"
                    (click)="applySavedView(view)"></button>
        }
        <span class="saved-view-save">
            <p-inputgroup class="saved-view-input">
                <p-inputgroup-addon><i class="pi pi-bookmark"></i></p-inputgroup-addon>
                <input pInputText type="text" placeholder="Saved view name" [(ngModel)]="savedViewName">
            </p-inputgroup>
            <p-button icon="pi pi-save" label="Save View" severity="secondary" [outlined]="true" (click)="saveCurrentView()"></p-button>
        </span>
    </div>

    <app-page-search-panel
        title="Sales order filters"
        [activeFilters]="activeFilterCount"
        [resultText]="totalRecords + ' matching records'"
        [collapsed]="true"
        (search)="applySearch()"
        (clear)="clearSearch()">
        <div class="workbench-filters">
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <input id="salesFreeText" pInputText type="text" [(ngModel)]="filterModel.freeText"
                           placeholder="Order, customer, salesperson">
                    <label for="salesFreeText">Search</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-select inputId="salesCustomer" [options]="customers" [(ngModel)]="filterModel.partyId"
                              optionLabel="name" optionValue="id" [filter]="true" [showClear]="true"
                              placeholder="Any customer" styleClass="w-full"></p-select>
                    <label for="salesCustomer">Customer</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-select inputId="salesSalesperson" [options]="salespersons" [(ngModel)]="filterModel.salespersonId"
                              optionLabel="name" optionValue="id" [filter]="true" [showClear]="true"
                              placeholder="Any salesperson" styleClass="w-full"></p-select>
                    <label for="salesSalesperson">Salesperson</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <input id="salesOrderNumber" pInputText type="text" [(ngModel)]="filterModel.orderNumber">
                    <label for="salesOrderNumber">Order number</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-tags"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-multiSelect inputId="salesStatuses" [options]="statusOptions" [(ngModel)]="filterModel.statuses"
                                   optionLabel="label" optionValue="value" display="chip" [showClear]="true"
                                   placeholder="Any status" styleClass="w-full"></p-multiSelect>
                    <label for="salesStatuses">Status</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-multiSelect inputId="salesApprovalStatuses" [options]="approvalStatusOptions" [(ngModel)]="filterModel.approvalStatuses"
                                   optionLabel="label" optionValue="value" display="chip" [showClear]="true"
                                   placeholder="Any approval" styleClass="w-full"></p-multiSelect>
                    <label for="salesApprovalStatuses">Approval</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-multiSelect inputId="salesFulfillmentStatuses" [options]="fulfillmentStatusOptions" [(ngModel)]="filterModel.fulfillmentStatuses"
                                   optionLabel="label" optionValue="value" display="chip" [showClear]="true"
                                   placeholder="Any fulfillment" styleClass="w-full"></p-multiSelect>
                    <label for="salesFulfillmentStatuses">Fulfillment</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-multiSelect inputId="salesBillingStatuses" [options]="billingStatusOptions" [(ngModel)]="filterModel.billingStatuses"
                                   optionLabel="label" optionValue="value" display="chip" [showClear]="true"
                                   placeholder="Any billing" styleClass="w-full"></p-multiSelect>
                    <label for="salesBillingStatuses">Billing</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-lock"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-multiSelect inputId="salesHoldStatuses" [options]="holdStatusOptions" [(ngModel)]="filterModel.holdStatuses"
                                   optionLabel="label" optionValue="value" display="chip" [showClear]="true"
                                   placeholder="Any hold" styleClass="w-full"></p-multiSelect>
                    <label for="salesHoldStatuses">Hold</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-select inputId="salesProduct" [options]="products" [(ngModel)]="filterModel.productId"
                              optionLabel="name" optionValue="productID" [filter]="true" [showClear]="true"
                              placeholder="Any product" styleClass="w-full"></p-select>
                    <label for="salesProduct">Product</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-briefcase"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <app-logistics-project-lookup inputId="salesProject" [(ngModel)]="filterModel.logisticsProjectId" placeholder="Any project"></app-logistics-project-lookup>
                    <label for="salesProject">Logistics Project</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-datepicker inputId="salesOrderFrom" [(ngModel)]="filterModel.orderDateFrom" dateFormat="yy-mm-dd"
                                  [showIcon]="true" styleClass="w-full"></p-datepicker>
                    <label for="salesOrderFrom">Order from</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-calendar-plus"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-datepicker inputId="salesOrderTo" [(ngModel)]="filterModel.orderDateTo" dateFormat="yy-mm-dd"
                                  [showIcon]="true" styleClass="w-full"></p-datepicker>
                    <label for="salesOrderTo">Order to</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-clock"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-datepicker inputId="salesExpectedFrom" [(ngModel)]="filterModel.expectedDateFrom" dateFormat="yy-mm-dd"
                                  [showIcon]="true" styleClass="w-full"></p-datepicker>
                    <label for="salesExpectedFrom">Expected from</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-clock"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-datepicker inputId="salesExpectedTo" [(ngModel)]="filterModel.expectedDateTo" dateFormat="yy-mm-dd"
                                  [showIcon]="true" styleClass="w-full"></p-datepicker>
                    <label for="salesExpectedTo">Expected to</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-sort-amount-up"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-inputNumber inputId="salesAmountMin" [(ngModel)]="filterModel.amountMin" mode="decimal"
                                   [minFractionDigits]="2" styleClass="w-full"></p-inputNumber>
                    <label for="salesAmountMin">Min amount</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-sort-amount-down"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-inputNumber inputId="salesAmountMax" [(ngModel)]="filterModel.amountMax" mode="decimal"
                                   [minFractionDigits]="2" styleClass="w-full"></p-inputNumber>
                    <label for="salesAmountMax">Max amount</label>
                </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
                <p-inputgroup-addon><i class="pi pi-globe"></i></p-inputgroup-addon>
                <p-iftalabel>
                    <p-select inputId="salesCurrency" [options]="currencyOptions" [(ngModel)]="filterModel.currencyCode"
                              optionLabel="label" optionValue="value" [showClear]="true"
                              placeholder="Any currency" styleClass="w-full"></p-select>
                    <label for="salesCurrency">Currency</label>
                </p-iftalabel>
            </p-inputgroup>
        </div>
    </app-page-search-panel>

    @if (selectedSalesOrders.length) {
        <p-toolbar styleClass="bulk-toolbar">
            <ng-template pTemplate="start">
                <span>{{ selectedSalesOrders.length }} selected</span>
            </ng-template>
            <ng-template pTemplate="end">
                <p-button label="Export Selected" icon="pi pi-download" severity="secondary" (click)="exportOrders(true)"></p-button>
            </ng-template>
        </p-toolbar>
    }

    <app-page-data-panel title="Sales Orders" [resultText]="totalRecords + ' records'">
        <p-table [value]="salesOrders" dataKey="id" [(selection)]="selectedSalesOrders" [resizableColumns]="true"
            styleClass="p-datatable-gridlines p-datatable-sm order-workbench-table" [tableStyle]="{ 'min-width': '74rem' }" [paginator]="true"
            [rows]="rows" [first]="first" [showCurrentPageReport]="true" [totalRecords]="totalRecords" [lazy]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" (onLazyLoad)="loadData($event)"
            [rowsPerPageOptions]="[20, 50, 100]">
            <ng-template pTemplate="header">
                <tr>
                    <th style="width:3rem"><p-tableHeaderCheckbox></p-tableHeaderCheckbox></th>
                    <th class="action-col">Actions</th>
                    <th pSortableColumn="orderNumber">Order <p-sortIcon field="orderNumber"></p-sortIcon></th>
                    <th pSortableColumn="party">Customer Context <p-sortIcon field="party"></p-sortIcon></th>
                    <th>Project Context</th>
                    <th pSortableColumn="orderDate">Schedule <p-sortIcon field="orderDate"></p-sortIcon></th>
                    <th pSortableColumn="totalAmount">Lifecycle / Value <p-sortIcon field="totalAmount"></p-sortIcon></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-salesOrder>
                <tr>
                    <td><p-tableCheckbox [value]="salesOrder"></p-tableCheckbox></td>
                    <td class="action-col">
                        <div class="action-stack">
                            <button pButton type="button" icon="pi pi-eye"
                                [routerLink]="['/order/sales-order/view', salesOrder.id]" pTooltip="View order"
                                class="p-button-rounded p-button-text p-button-primary"></button>
                            @if (canConfirm(salesOrder)) {
                                <button pButton type="button" icon="pi pi-check" (click)="confirmSalesOrder(salesOrder)"
                                    pTooltip="Confirm order" class="p-button-rounded p-button-text p-button-success"></button>
                            }
                            <button pButton type="button" icon="pi pi-pencil"
                                [routerLink]="['/order/sales-order/edit', salesOrder.id]"
                                (click)="editSalesOrder(salesOrder)"
                                pTooltip="Edit order" class="p-button-rounded p-button-text p-button-info"></button>
                        </div>
                    </td>
                    <td>
                        <div class="cell-title">{{ salesOrder.orderNumber }}</div>
                        @if (salesOrder.externalReferenceNumber) {
                            <div class="cell-meta mt-1">Ref: {{ salesOrder.externalReferenceNumber }}</div>
                        }
                        <div class="cell-meta mt-1">Sales order</div>
                        <div class="cell-meta mt-1">Currency: {{ salesOrder.currencyCode || '-' }}</div>
                    </td>
                    <td>
                        <div class="cell-title">{{ salesOrder.partyName || '-' }}</div>
                        <div class="cell-meta mt-1">Customer account</div>
                        <div class="cell-meta mt-1">Salesperson: {{ salesOrder.salespersonName || '-' }}</div>
                    </td>
                    <td>
                        @if (salesOrder.logisticsProjectId) {
                            <button type="button" class="context-link"
                                    [routerLink]="['/logistics-projects', salesOrder.logisticsProjectId]"
                                    pTooltip="Open linked logistics project">
                                <i class="pi pi-briefcase"></i>
                                <span>{{ projectLabel(salesOrder.logisticsProjectId) }}</span>
                            </button>
                            <div class="cell-meta mt-2">Linked logistics project</div>
                        } @else {
                            <div class="cell-title">No project assigned</div>
                            <div class="cell-meta mt-1">Order is not linked to a logistics project.</div>
                        }
                    </td>
                    <td>
                        <div class="cell-title">Order: {{ salesOrder.orderDate | date:'dd MMM yyyy' }}</div>
                        <div class="cell-meta mt-1">Expected: {{ salesOrder.expectedDate | date:'dd MMM yyyy' }}</div>
                        <div class="cell-meta mt-1">Terms: {{ salesOrder.paymentTermName || '-' }}</div>
                    </td>
                    <td>
                        <div class="cell-title">{{ getSalesOrderTotalAmount(salesOrder) }}</div>
                        <div class="tag-row mt-2">
                            <p-badge [severity]="statusSeverity(salesOrder.state)"
                                     [value]="statusLabel(salesOrder.state)"
                                     [pTooltip]="statusTooltip(salesOrder.state)"></p-badge>
                            <p-badge [severity]="statusSeverity(salesOrder.approvalStatus)"
                                     [value]="statusLabel(salesOrder.approvalStatus)"></p-badge>
                            <p-badge [severity]="statusSeverity(salesOrder.fulfillmentStatus)"
                                     [value]="statusLabel(salesOrder.fulfillmentStatus)"></p-badge>
                            <p-badge [severity]="statusSeverity(salesOrder.billingStatus)"
                                     [value]="statusLabel(salesOrder.billingStatus)"></p-badge>
                        </div>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="7">No sales orders match the current filters.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>
